import { useState } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import { ChevronDown, ChevronUp, CircleHelp, LogOut, User } from 'lucide-react'
import { cn } from '@/utils/cn'
import { useAuthStore } from '@/store/authStore'
import { useSessionStore } from '@/store/sessionStore'
import { usePermissions } from '@/hooks/usePermissions'
import { getProfilePictureUrl } from '@/services/user'

const DISABLE_AUTH = import.meta.env.VITE_DISABLE_AUTH === 'true'

/**
 * Header.tsx
 * Exact port of Blazor Shared/Components/Header.razor.
 *
 * Structure (left → right):
 *   [Logo] | [App name] [Tenant] | [Dashboard] [Capacity Mgmt] [Attendance] [Reports]
 *                                  [Help ▾]  [Avatar ▾]
 *
 * Role-gated tabs match Blazor RoleManager.UserHasPermission checks exactly:
 *   Dashboard + Capacity Management : CapacityApplication AND tenant = DUWL
 *   Attendance Tracking + Reports   : AttendanceTrackingApplication
 *
 * User dropdown items:
 *   User Management  : UserManagement permission
 *   DU Staff Badging : DUStaffBadging permission AND tenant = DUWL
 *   Log Out          : always
 *
 * Logo: requires public/assets/imgs/logo.png
 *   → copy from Blazor: wwwroot/assets/imgs/logo.png
 */
export function Header() {
  const logoSrc = `${import.meta.env.BASE_URL}assets/imgs/logo.png`
  // Always call useMsal() unconditionally.
  // In DISABLE_AUTH mode, MockAuthProvider ensures it's safe.
  // In production mode, MsalProvider ensures it's safe.
  const msalContext = useMsal()
  const navigate = useNavigate()
  const location     = useLocation()
  const { hasPermission } = usePermissions()
  const { displayName, tenant, email, availableTenants } = useAuthStore()
  const setTenant    = useAuthStore((s) => s.setTenant)
  const setSessionKey = useSessionStore((s) => s.set)
  const accounts     = msalContext.accounts ?? []

  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showHelpMenu, setShowHelpMenu] = useState(false)
  const [showTenantMenu, setShowTenantMenu] = useState(false)

  // ── Derived values ──────────────────────────────────────────────────────────
  const userName     = accounts[0]?.name ?? displayName ?? accounts[0]?.username ?? ''
  const userInitials = userName.split(' ').filter(Boolean).map((n) => n[0]).join('').slice(0, 2).toUpperCase()
  // Profile photo URL from Profiles API (matches Blazor ApplicationConfigs.ProfilesAPI)
  const userEmail    = accounts[0]?.username ?? email ?? ''
  const profileUrl   = getProfilePictureUrl(userEmail)
  // Default to DUWL when no tenant is set (matches Blazor default: Tenant = "DUWL")
  const activeTenant = tenant || 'DUWL'
  const isDuwl       = activeTenant === 'DUWL'

  /**
   * Mirrors Blazor Header.razor showRegionOption:
   *   UserTenants.Count > 1 — but NOT shown on access_denied / certain public pages.
   */
  const isAccessDeniedPage = location.pathname.toLowerCase().includes('access_denied')
  const showRegionOption   = availableTenants.length > 1 && !isAccessDeniedPage

  // ── Permission gates (mirror Blazor RoleManager checks) ────────────────────
  const canAccessCapacity    = hasPermission('CapacityApplication')
  const canAccessAttendance  = hasPermission('AttendanceTrackingApplication')
  const canManageUsers       = hasPermission('UserManagement')
  const canStaffBadge        = hasPermission('DUStaffBadging') && isDuwl

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const closeAll     = () => { setShowUserMenu(false); setShowHelpMenu(false); setShowTenantMenu(false) }
  const handleLogout = () => { closeAll(); if (!DISABLE_AUTH) void msalContext.instance.logoutRedirect() }

  /**
   * Mirrors Blazor Header.razor GotoURL() + SetSession():
   *   SetSession saves tenant to session storage, closes region menu.
   *   GotoURL navigates:
   *     DUIN → /attendancetracking
   *     DUWL → /dashboard (CapacityApplication) OR /attendancetracking OR /access_denied
   */
  const handleTenantChange = (newTenant: string) => {
    // Update stores (mirrors Blazor SetSession + Blazored.SessionStorage)
    setTenant(newTenant)
    setSessionKey('tenant', newTenant)
    closeAll()
    // Navigate (mirrors Blazor GotoURL — no forceLoad needed in React SPA)
    if (newTenant === 'DUIN') {
      navigate('/attendancetracking')
    } else if (newTenant === 'DUWL') {
      if (hasPermission('CapacityApplication')) {
        navigate('/dashboard')
      } else if (hasPermission('AttendanceTrackingApplication')) {
        navigate('/attendancetracking')
      } else {
        navigate('/access_denied')
      }
    }
  }

  // Shared NavLink active class — matches Blazor .navactive CSS
  const tabClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'flex cursor-pointer items-center px-3 text-[14px] text-white no-underline outline-none',
      isActive ? 'bg-[#26890D] my-1 self-stretch' : 'h-full hover:bg-white/10',
    )

  return (
    <>
      {/* ── Fixed top bar ──────────────────────────────────────────────────── */}
      <header
        className="fixed left-0 top-0 z-[160] flex h-14 w-full items-center justify-between bg-[#080808]"
        aria-label="Application header"
      >
        {/* ── Left: logo + app name + nav tabs (80%) ─────────────────────── */}
        <div className="flex h-full w-4/5 items-center">

          {/* Logo */}
          <div className="flex h-full items-center mx-4">
            <img
              src={logoSrc}
              alt="Deloitte"
              className="h-4 align-baseline"
              onError={(e) => {
                // Fallback text when logo asset is not yet copied to public/
                const el = e.currentTarget
                el.style.display = 'none'
                const parent = el.parentElement
                if (parent && !parent.querySelector('[data-logo-fallback]')) {
                  const span = document.createElement('span')
                  span.setAttribute('data-logo-fallback', 'true')
                  span.className = 'text-sm font-bold text-white tracking-widest'
                  span.textContent = 'DELOITTE'
                  parent.appendChild(span)
                }
              }}
            />
          </div>

          {/* App name + tenant — border-left separator matches Blazor .application-sub-title-container */}
          <div className="flex h-full items-center border-l border-[#53565a]">
            <span className="ml-[15px] text-[16px] text-white">SpaceIQ</span>

            {/*
             * Tenant: dropdown when user has multiple tenants (showRegionOption),
             * static text when only one tenant.
             * Mirrors Blazor Header.razor @if (showRegionOption) { dropdown } else { label }
             */}
            {showRegionOption ? (
              /* Multi-tenant: dropdown toggle + menu */
              <div className="relative ml-5">
                <button
                  type="button"
                  className="flex cursor-pointer items-center gap-1 border-0 bg-transparent text-white"
                  onClick={() => setShowTenantMenu((v) => !v)}
                  aria-label={`Selected region: ${activeTenant}. Click to change.`}
                  aria-expanded={showTenantMenu}
                  aria-haspopup="listbox"
                >
                  <span className="text-[13px]">{activeTenant}</span>
                  {showTenantMenu ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>

                {showTenantMenu && (
                  <ul
                    className="absolute left-0 top-full z-[200] min-w-[80px] list-none bg-white py-[5px] shadow-[0_2px_4px_0_rgba(0,0,0,0.12)] border border-[#e5e5e5] m-0 p-0"
                    role="listbox"
                    aria-label="Select region"
                  >
                    {availableTenants.map((t) => (
                      <li
                        key={t}
                        role="option"
                        aria-selected={t === activeTenant}
                        className={cn(
                          'cursor-pointer px-4 py-[6px] text-[13px] text-black hover:bg-[#f6f6f6]',
                          t === activeTenant && 'font-semibold',
                        )}
                        onClick={() => handleTenantChange(t)}
                      >
                        {t}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              /* Single tenant: static label — matches Blazor Region-menu-title */
              activeTenant && (
                <span className="ml-5 text-[13px] text-white">{activeTenant}</span>
              )
            )}
          </div>

          {/* Nav tabs */}
          <nav className="flex h-full items-center ml-10" aria-label="Main navigation">
            {canAccessCapacity && isDuwl && (
              <>
                <NavLink to="/dashboard"          className={tabClass}>Dashboard</NavLink>
                <NavLink to="/capacity-management" className={tabClass}>Capacity Management</NavLink>
              </>
            )}
            {canAccessAttendance && (
              <>
                <NavLink to="/attendancetracking" className={tabClass}>Attendance Tracking</NavLink>
                <NavLink to="/attendancesummary"  className={tabClass}>Reports</NavLink>
              </>
            )}
          </nav>
        </div>

        {/* ── Right: help + user (20%) ────────────────────────────────────── */}
        <div className="flex h-full w-1/5 items-center justify-end">

          {/* Help icon — matches Blazor .th-header-sub-container.question-menu */}
          <div className="relative mx-[10px]">
            <button
              type="button"
              className="flex cursor-pointer items-center border-0 bg-transparent text-white"
              onClick={() => { setShowHelpMenu((v) => !v); setShowUserMenu(false) }}
              aria-label="Have a Question"
              aria-expanded={showHelpMenu}
              aria-haspopup="true"
              title="Have a Question"
            >
              <CircleHelp size={20} />
            </button>

            {showHelpMenu && (
              <div
                className="fixed top-14 z-[200] min-w-[170px] bg-white border border-[#e5e5e5] py-[5px] shadow-[0_2px_4px_0_rgba(0,0,0,0.12)]"
                style={{ right: '90px' }}
                role="menu"
              >
                <ul className="m-0 list-none p-0">
                  <li role="none">
                    <a
                      href="https://deloitteus.service-now.com/sp?id=sc_cat_item&sys_id=9d997dc0db16ac10eb90abc5ca961911&table=sc_cat_item"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block no-underline hover:bg-[#f6f6f6]"
                      role="menuitem"
                    >
                      <span className="block py-[2px] text-[13px] leading-5 text-black ml-[15px]">Raise an Incident</span>
                    </a>
                  </li>
                </ul>
              </div>
            )}
          </div>

          {/* User avatar + dropdown — matches Blazor #user-dropdown */}
          <div className="relative mx-5">
            <button
              type="button"
              className="flex cursor-pointer items-center border-0 bg-transparent"
              onClick={() => { setShowUserMenu((v) => !v); setShowHelpMenu(false) }}
              aria-label="User menu"
              aria-expanded={showUserMenu}
              aria-haspopup="true"
            >
              {/* Avatar: profile photo from Profiles API; falls back to initials circle */}
              {profileUrl ? (
                <img
                  src={profileUrl}
                  alt={userName}
                  aria-hidden="true"
                  className="h-[35px] w-[35px] rounded-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                    const sibling = e.currentTarget.nextElementSibling as HTMLElement | null
                    if (sibling) sibling.style.display = 'flex'
                  }}
                />
              ) : null}
              <div
                aria-hidden="true"
                className="flex h-[35px] w-[35px] items-center justify-center rounded-full bg-[#53565a] text-sm font-medium text-white"
                style={{ display: profileUrl ? 'none' : 'flex' }}
              >
                {userInitials ? userInitials : <User size={18} />}
              </div>
              <span className="ml-1 text-white">
                {showUserMenu ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </span>
            </button>

            {showUserMenu && (
              <div
                className="fixed top-14 z-[200] min-w-[240px] rounded-[10px] border border-[#e5e5e5] bg-white shadow-[0_2px_4px_0_rgba(0,0,0,0.12)]"
                style={{ right: '25px' }}
                role="menu"
              >
                <ul className="m-0 list-none p-0">
                  {/* User identity row */}
                  <li className="p-[10px]" role="none">
                    <div className="flex items-center">
                      {/* Profile photo in dropdown — same source as header button avatar */}
                      {profileUrl ? (
                        <img
                          src={profileUrl}
                          alt={userName}
                          className="h-[35px] w-[35px] shrink-0 rounded-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none'
                            const sibling = e.currentTarget.nextElementSibling as HTMLElement | null
                            if (sibling) sibling.style.removeProperty('display')
                          }}
                        />
                      ) : null}
                      <div
                        className="flex h-[35px] w-[35px] shrink-0 items-center justify-center rounded-full bg-[#53565a] text-sm font-medium text-white"
                        style={{ display: profileUrl ? 'none' : undefined }}
                      >
                        {userInitials ? userInitials : <User size={18} />}
                      </div>
                      <div className="ml-[15px]">
                        <div className="text-[13px] font-semibold leading-5 text-black">{userName}</div>
                        <div className="text-[12px] leading-5 text-[#97999b]">Talent</div>
                      </div>
                    </div>
                  </li>

                  {/* User Management */}
                  {canManageUsers && (
                    <li
                      className="cursor-pointer p-[10px] hover:bg-[#f6f6f6]"
                      role="none"
                    >
                      <button
                        type="button"
                        className="w-full border-0 bg-transparent text-left"
                        role="menuitem"
                        onClick={() => { navigate('/user-access-management'); closeAll() }}
                      >
                        <span className="ml-[15px] text-[13px] leading-5 text-black">User Management</span>
                      </button>
                    </li>
                  )}

                  {/* DU Staff Badging */}
                  {canStaffBadge && (
                    <li
                      className="cursor-pointer p-[10px] hover:bg-[#f6f6f6]"
                      role="none"
                    >
                      <button
                        type="button"
                        className="w-full border-0 bg-transparent text-left"
                        role="menuitem"
                        onClick={() => { navigate('/staff-badging'); closeAll() }}
                      >
                        <span className="ml-[15px] text-[13px] leading-5 text-black">DU Staff Badging</span>
                      </button>
                    </li>
                  )}

                  {/* Separator */}
                  <li className="mx-[5%] border-t border-[#e6e6e6]" role="separator" />

                  {/* Log Out */}
                  <li
                    className="cursor-pointer p-[10px] hover:bg-[#f6f6f6]"
                    role="none"
                  >
                    <button
                      type="button"
                      className="flex w-full items-center border-0 bg-transparent text-left"
                      role="menuitem"
                      onClick={handleLogout}
                    >
                      <LogOut size={16} className="text-black" aria-hidden="true" />
                      <span className="ml-[15px] text-[13px] leading-5 text-black">Log Out</span>
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Click-away backdrop — z between page (0) and dropdown (200) */}
      {(showUserMenu || showHelpMenu || showTenantMenu) && (
        <div
          className="fixed inset-0 z-[150]"
          onClick={closeAll}
          aria-hidden="true"
        />
      )}
    </>
  )
}
